/**
 * QUIPWA — Outbox
 *
 * A persistent queue of writes that must reach Drive. Every local-first write
 * (save a note, upload a deck, trash a file…) goes here first; the sync engine
 * drains it whenever there is a connection and a valid session.
 *
 * Properties:
 *  - **Durable**: it lives in the device-local SQLite, so it survives reloads,
 *    closed tabs and days offline.
 *  - **Coalescing**: one pending op per (kind, entityKey). Saving a note ten
 *    times while offline uploads it once, with the last content.
 *  - **Safe against races**: `complete` only deletes an op if it was not
 *    re-enqueued while being processed (`revision` check).
 *  - **Backoff**: failed ops are retried later (`fail` sets next_attempt_at).
 *
 * ```ts
 * await outbox.enqueue('note.upsert', noteId, { noteId })
 * for (const op of await outbox.due()) { … await outbox.complete(op) }
 * ```
 */
import type { IDBAdapter } from '../db/db-adapter.ts'

export interface OutboxOp<P = unknown> {
  id: number
  kind: string
  entityKey: string
  payload: P
  revision: number
  createdAt: number
  attempts: number
  nextAttemptAt: number
  lastError: string | null
}

interface OutboxRow {
  id: number
  kind: string
  entity_key: string
  payload: string
  revision: number
  created_at: number
  attempts: number
  next_attempt_at: number
  last_error: string | null
}

/** After this many failures an op is parked: kept for visibility, no longer retried automatically. */
export const MAX_OUTBOX_ATTEMPTS = 8
const PARKED = Number.MAX_SAFE_INTEGER

function toOp<P>(row: OutboxRow): OutboxOp<P> {
  return {
    id: row.id,
    kind: row.kind,
    entityKey: row.entity_key,
    payload: JSON.parse(row.payload) as P,
    revision: row.revision,
    createdAt: row.created_at,
    attempts: row.attempts,
    nextAttemptAt: row.next_attempt_at,
    lastError: row.last_error,
  }
}

/** Retry delay after `attempts` failures: 15 s, 30 s, 1 min… capped at 30 min. */
export function outboxBackoffMs(attempts: number): number {
  return Math.min(30 * 60_000, 15_000 * 2 ** Math.max(0, attempts - 1))
}

export class Outbox {
  private readonly db: IDBAdapter
  private readonly now: () => number
  private readonly listeners = new Set<() => void>()

  constructor(db: IDBAdapter, now: () => number = Date.now) {
    this.db = db
    this.now = now
  }

  /** Add or replace the pending op for (kind, entityKey). */
  async enqueue<P>(kind: string, entityKey: string, payload: P): Promise<void> {
    await this.db.run(
      `INSERT INTO outbox (kind, entity_key, payload, revision, created_at, attempts, next_attempt_at, last_error)
       VALUES (?, ?, ?, 1, ?, 0, 0, NULL)
       ON CONFLICT(kind, entity_key) DO UPDATE SET
         payload = excluded.payload,
         revision = outbox.revision + 1,
         created_at = excluded.created_at,
         attempts = 0,
         next_attempt_at = 0,
         last_error = NULL`,
      [kind, entityKey, JSON.stringify(payload), this.now()]
    )
    this.emit()
  }

  /** Ops ready to run now, oldest first. */
  async due(limit = 50): Promise<OutboxOp[]> {
    const rows = await this.db.query<OutboxRow>(
      'SELECT * FROM outbox WHERE next_attempt_at <= ? ORDER BY id ASC LIMIT ?',
      [this.now(), limit]
    )
    return rows.map((r) => toOp(r))
  }

  /** Every pending op (including parked ones), for the UI. */
  async all(): Promise<OutboxOp[]> {
    return (await this.db.query<OutboxRow>('SELECT * FROM outbox ORDER BY id ASC')).map((r) => toOp(r))
  }

  async find<P>(kind: string, entityKey: string): Promise<OutboxOp<P> | null> {
    const row = await this.db.queryOne<OutboxRow>('SELECT * FROM outbox WHERE kind = ? AND entity_key = ?', [kind, entityKey])
    return row ? toOp<P>(row) : null
  }

  /** Done. Ignored if the op was re-enqueued meanwhile (a newer payload is waiting). */
  async complete(op: Pick<OutboxOp, 'id' | 'revision'>): Promise<void> {
    await this.db.run('DELETE FROM outbox WHERE id = ? AND revision = ?', [op.id, op.revision])
    this.emit()
  }

  /** Record a failure and schedule the retry (or park the op after too many). */
  async fail(op: Pick<OutboxOp, 'id' | 'revision' | 'attempts'>, error: string, retryInMs?: number): Promise<void> {
    const attempts = op.attempts + 1
    const nextAttemptAt = attempts >= MAX_OUTBOX_ATTEMPTS ? PARKED : this.now() + (retryInMs ?? outboxBackoffMs(attempts))
    await this.db.run(
      'UPDATE outbox SET attempts = ?, next_attempt_at = ?, last_error = ? WHERE id = ? AND revision = ?',
      [attempts, nextAttemptAt, error.slice(0, 500), op.id, op.revision]
    )
    this.emit()
  }

  /** Make every op (parked ones included) eligible again — "Retry now". */
  async retryAll(): Promise<void> {
    await this.db.run('UPDATE outbox SET next_attempt_at = 0, attempts = 0')
    this.emit()
  }

  async remove(kind: string, entityKey: string): Promise<void> {
    await this.db.run('DELETE FROM outbox WHERE kind = ? AND entity_key = ?', [kind, entityKey])
    this.emit()
  }

  async count(): Promise<{ pending: number; parked: number }> {
    const row = await this.db.queryOne<{ pending: number; parked: number }>(
      `SELECT COUNT(*) FILTER (WHERE next_attempt_at < ?) AS pending,
              COUNT(*) FILTER (WHERE next_attempt_at >= ?) AS parked
       FROM outbox`,
      [PARKED, PARKED]
    )
    return { pending: row?.pending ?? 0, parked: row?.parked ?? 0 }
  }

  /** Notified after every change to the queue. */
  onChange(listener: () => void): () => void {
    this.listeners.add(listener)
    return () => this.listeners.delete(listener)
  }

  private emit(): void {
    for (const l of this.listeners) l()
  }
}
